/**
 * EngagementImport — Wizard 4 étapes : Dépôt → OCR IA → Analyse → Enregistré.
 * Utilise Gemini Vision via Emergent LLM key (backend /engagements/ocr/extract).
 */
import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, FileText, Loader2, CheckCircle, Sparkles, ArrowRight, Save } from "lucide-react";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import { CATEGORIES } from "@/lib/engagements-categories";

const STEPS = [
  { id: 1, label: "Dépôt", icon: Upload },
  { id: 2, label: "OCR IA", icon: Sparkles },
  { id: 3, label: "Analyse", icon: FileText },
  { id: 4, label: "Enregistré", icon: CheckCircle },
];

const MODES = [
  { value: "lrar", label: "LRAR (Recommandée AR)" },
  { value: "lettre_simple", label: "Lettre simple" },
  { value: "email", label: "Email" },
  { value: "autre", label: "Autre" },
];

export default function EngagementImport() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({});

  const onDrop = useCallback((accepted) => {
    if (!accepted?.length) return;
    setFile(accepted[0]);
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, accept: { "application/pdf": [".pdf"], "image/png": [".png"], "image/jpeg": [".jpg", ".jpeg"] },
    maxFiles: 1, maxSize: 15 * 1024 * 1024,
  });

  const runOCR = async () => {
    if (!file) return toast.error("Aucun fichier");
    setStep(2); setLoading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/engagements/ocr/extract", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
      setForm({ ...r.data, prestataire: r.data.prestataire || r.data.nom_prestataire || "" });
      setStep(3);
      toast.success("Extraction IA terminée");
    } catch (e) {
      toast.error(apiError(e));
      setStep(1);
    } finally { setLoading(false); }
  };

  const save = async () => {
    setLoading(true);
    try {
      // Garde le PDF original (base64) pour l'attacher au contrat côté backend
      const { preavis_statut, ...payload } = form;
      const r = await api.post("/engagements/contrats", payload);
      toast.success("Contrat enregistré (PDF attaché)");
      setStep(4);
      setTimeout(() => navigate(`/app/depenses/contrats/${r.data.id}`), 1000);
    } catch (e) {
      toast.error(apiError(e));
    } finally { setLoading(false); }
  };

  const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // Bidirectionnel mensuel ↔ annuel (P0)
  const updMensuel = (v) => {
    const num = parseFloat(v);
    setForm(prev => ({
      ...prev,
      montant_mensuel_ht: isNaN(num) ? 0 : num,
      montant_annuel_ht: isNaN(num) ? 0 : Math.round(num * 12 * 100) / 100,
    }));
  };
  const updAnnuel = (v) => {
    const num = parseFloat(v);
    setForm(prev => ({
      ...prev,
      montant_annuel_ht: isNaN(num) ? 0 : num,
      montant_mensuel_ht: isNaN(num) ? 0 : Math.round((num / 12) * 100) / 100,
    }));
  };

  const isPret = ["pret", "credit"].includes(form.categorie);

  return (
    <div className="space-y-6 max-w-4xl" data-testid="engagement-import-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Importer un contrat</h1>
        <p className="text-sm text-slate-500">L'IA extrait automatiquement les montants, dates et clauses</p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s.id} className="flex items-center gap-2 flex-1">
            <div className={`flex items-center gap-2 ${step >= s.id ? "text-blue-600" : "text-slate-400"}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= s.id ? "bg-blue-100" : "bg-slate-100"}`}>
                {step > s.id ? <CheckCircle className="w-5 h-5" /> : <s.icon className="w-4 h-4" />}
              </div>
              <span className="text-xs font-medium hidden sm:inline">{s.label}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 ${step > s.id ? "bg-blue-600" : "bg-slate-200"}`} />}
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {/* ---- ÉTAPE 1 : DÉPÔT ---- */}
        {step === 1 && (
          <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <Card>
              <CardContent className="p-6">
                <div {...getRootProps()} className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${isDragActive ? "border-blue-500 bg-blue-50" : "border-slate-300 hover:border-slate-400"}`} data-testid="dropzone">
                  <input {...getInputProps()} id="engagement-dropzone-input" name="engagement-dropzone-input" />
                  <Upload className="w-12 h-12 mx-auto text-slate-400 mb-3" />
                  {file ? (
                    <>
                      <p className="font-medium text-slate-900">{file.name}</p>
                      <p className="text-xs text-slate-500 mt-1">{(file.size / 1024 / 1024).toFixed(1)} Mo</p>
                    </>
                  ) : (
                    <>
                      <p className="font-medium text-slate-700">Déposez un contrat PDF ou image</p>
                      <p className="text-xs text-slate-500 mt-1">ou cliquez pour parcourir · Max 15 Mo</p>
                    </>
                  )}
                </div>
                <div className="mt-4 flex justify-end">
                  <Button onClick={runOCR} disabled={!file} data-testid="run-ocr-btn">
                    <Sparkles className="w-4 h-4 mr-2" />Analyser avec IA<ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* ---- ÉTAPE 2 : OCR ---- */}
        {step === 2 && (
          <motion.div key="s2" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Card><CardContent className="p-12 text-center">
              <Loader2 className="w-12 h-12 mx-auto text-blue-600 animate-spin mb-4" />
              <p className="font-medium text-slate-900">Analyse IA en cours...</p>
              <p className="text-sm text-slate-500 mt-1">Extraction des montants, dates et clauses</p>
            </CardContent></Card>
          </motion.div>
        )}

        {/* ---- ÉTAPE 3 : ANALYSE ---- */}
        {step === 3 && (
          <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4 text-blue-600" />Résultat de l'extraction IA</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Prestataire *</Label>
                  <Input value={form.prestataire || ""} onChange={e => upd("prestataire", e.target.value)} data-testid="form-prestataire" />
                </div>
                <div>
                  <Label>Nom du contrat *</Label>
                  <Input value={form.nom_contrat || ""} onChange={e => upd("nom_contrat", e.target.value)} data-testid="form-nom" />
                </div>
                <div>
                  <Label>Catégorie *</Label>
                  <Select value={form.categorie || "autre"} onValueChange={v => upd("categorie", v)}>
                    <SelectTrigger data-testid="form-categorie"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.emoji} {c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>N° contrat / police</Label>
                  <Input value={form.numero_contrat || ""} onChange={e => upd("numero_contrat", e.target.value)} />
                </div>
                <div>
                  <Label>Montant mensuel HT (€)</Label>
                  <Input type="number" step="0.01" value={form.montant_mensuel_ht || ""} onChange={e => updMensuel(e.target.value)} data-testid="form-mensuel" />
                  <p className="text-[10px] text-slate-400 mt-1">L'annuel se calcule automatiquement</p>
                </div>
                <div>
                  <Label>Montant annuel HT (€)</Label>
                  <Input type="number" step="0.01" value={form.montant_annuel_ht || ""} onChange={e => updAnnuel(e.target.value)} data-testid="form-annuel" />
                  <p className="text-[10px] text-slate-400 mt-1">Le mensuel se calcule automatiquement</p>
                </div>
                <div>
                  <Label>Date de début</Label>
                  <Input type="date" value={form.date_debut || ""} onChange={e => upd("date_debut", e.target.value)} />
                </div>
                <div>
                  <Label>Date d'échéance</Label>
                  <Input type="date" value={form.date_echeance || ""} onChange={e => upd("date_echeance", e.target.value)} />
                </div>
                <div>
                  <Label>Durée engagement (mois)</Label>
                  <Input type="number" value={form.duree_engagement_mois || 12} onChange={e => upd("duree_engagement_mois", parseInt(e.target.value) || 12)} />
                </div>
                <div>
                  <Label>Délai préavis (jours)</Label>
                  <Input type="number" value={form.delai_preavis_jours || 60} onChange={e => upd("delai_preavis_jours", parseInt(e.target.value) || 60)} />
                </div>
                <div>
                  <Label>Mode de résiliation</Label>
                  <Select value={form.mode_resiliation || "lrar"} onValueChange={v => upd("mode_resiliation", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MODES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2 flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <input type="checkbox" id="tacite" checked={!!form.reconduction_tacite}
                              onChange={e => upd("reconduction_tacite", e.target.checked)}
                              className="mt-0.5 w-4 h-4 rounded border-slate-300" data-testid="form-tacite" />
                  <div className="flex-1">
                    <Label htmlFor="tacite" className="cursor-pointer font-medium">🔁 Reconduction tacite</Label>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Si activée, le contrat repart pour une nouvelle période à chaque échéance sauf préavis respecté.
                    </p>
                  </div>
                </div>
                {form.reconduction_tacite && (<>
                  <div>
                    <Label>Durée reconduction (mois)</Label>
                    <Input type="number" value={form.duree_reconduction_mois || ""} placeholder="12" onChange={e => upd("duree_reconduction_mois", parseInt(e.target.value) || null)} />
                  </div>
                  <div>
                    <Label>Préavis reconduction (jours)</Label>
                    <Input type="number" value={form.preavis_reconduction_jours || ""} placeholder="60" onChange={e => upd("preavis_reconduction_jours", parseInt(e.target.value) || null)} />
                  </div>
                </>)}
                <div>
                  <Label>Email prestataire</Label>
                  <Input type="email" value={form.email_prestataire || ""} onChange={e => upd("email_prestataire", e.target.value)} />
                </div>
                <div>
                  <Label>Téléphone prestataire</Label>
                  <Input value={form.telephone_prestataire || ""} onChange={e => upd("telephone_prestataire", e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <Label>Adresse prestataire</Label>
                  <Textarea value={form.adresse_prestataire || ""} onChange={e => upd("adresse_prestataire", e.target.value)} rows={2} />
                </div>

                {/* --- Champs prêt / crédit --- */}
                {isPret && (<>
                  <div className="md:col-span-2 border-t pt-3 mt-2">
                    <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">💰 Détails prêt / crédit</p>
                  </div>
                  <div>
                    <Label>Capital emprunté (€)</Label>
                    <Input type="number" step="0.01" value={form.capital_emprunte || ""} onChange={e => upd("capital_emprunte", parseFloat(e.target.value) || 0)} data-testid="form-capital" />
                  </div>
                  <div>
                    <Label>Capital restant dû (€)</Label>
                    <Input type="number" step="0.01" value={form.capital_restant_du || ""} onChange={e => upd("capital_restant_du", parseFloat(e.target.value) || 0)} />
                  </div>
                  <div>
                    <Label>Taux d'intérêt (%)</Label>
                    <Input type="number" step="0.01" value={form.taux_interet || ""} onChange={e => upd("taux_interet", parseFloat(e.target.value) || 0)} />
                  </div>
                  <div>
                    <Label>TAEG (%)</Label>
                    <Input type="number" step="0.01" value={form.taux_effectif_global || ""} onChange={e => upd("taux_effectif_global", parseFloat(e.target.value) || 0)} data-testid="form-taeg" />
                  </div>
                  <div className="md:col-span-2 flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                    <input type="checkbox" id="remb-ok" checked={!!form.remboursement_anticipe_possible}
                                onChange={e => upd("remboursement_anticipe_possible", e.target.checked)}
                                className="mt-0.5 w-4 h-4 rounded border-slate-300" />
                    <Label htmlFor="remb-ok" className="cursor-pointer">Remboursement anticipé possible</Label>
                  </div>
                  {form.remboursement_anticipe_possible && (<>
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                      <input type="checkbox" id="remb-gratuit" checked={!!form.remboursement_anticipe_gratuit}
                                  onChange={e => upd("remboursement_anticipe_gratuit", e.target.checked)}
                                  className="w-4 h-4 rounded border-slate-300" />
                      <Label htmlFor="remb-gratuit" className="cursor-pointer">Sans indemnité (gratuit)</Label>
                    </div>
                    {!form.remboursement_anticipe_gratuit && (
                      <div>
                        <Label>Indemnité (%)</Label>
                        <Input type="number" step="0.01" value={form.remboursement_anticipe_indemnite_pct || ""} onChange={e => upd("remboursement_anticipe_indemnite_pct", parseFloat(e.target.value) || 0)} />
                      </div>
                    )}
                  </>)}
                  <div className="md:col-span-2 flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                    <input type="checkbox" id="ass-emp" checked={!!form.assurance_emprunteur_incluse}
                                onChange={e => upd("assurance_emprunteur_incluse", e.target.checked)}
                                className="mt-0.5 w-4 h-4 rounded border-slate-300" />
                    <Label htmlFor="ass-emp" className="cursor-pointer">Assurance emprunteur incluse</Label>
                  </div>
                  {form.assurance_emprunteur_incluse && (
                    <div>
                      <Label>Coût mensuel assurance emprunteur (€)</Label>
                      <Input type="number" step="0.01" value={form.montant_assurance_emprunteur_mensuel || ""} onChange={e => upd("montant_assurance_emprunteur_mensuel", parseFloat(e.target.value) || 0)} />
                    </div>
                  )}
                </>)}

                <div className="md:col-span-2">
                  <Label>Résumé IA</Label>
                  <Textarea value={form.resume_ia || ""} onChange={e => upd("resume_ia", e.target.value)} rows={4} data-testid="form-resume" />
                </div>
              </CardContent>
            </Card>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setStep(1)}>Retour</Button>
              <Button onClick={save} disabled={loading || !form.nom_contrat || !form.prestataire} data-testid="save-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Enregistrer
              </Button>
            </div>
          </motion.div>
        )}

        {/* ---- ÉTAPE 4 : SUCCÈS ---- */}
        {step === 4 && (
          <motion.div key="s4" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
            <Card><CardContent className="p-12 text-center">
              <CheckCircle className="w-16 h-16 mx-auto text-emerald-500 mb-4" />
              <p className="font-medium text-slate-900">Contrat enregistré</p>
              <p className="text-sm text-slate-500 mt-1">Redirection...</p>
            </CardContent></Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
